import { ApiRoutes } from "../../../Constants/ApiRoutingConstants";
import axios, { AxiosInstance } from "axios";
import authorizeService from "../../Identity/Utilities/AuthorizeService";
import AssetImageReadDto from "../../../Models/Assets/AssetImageReadDto";
import AssetTemplateReadDto from "../../../Models/Assets/AssetTemplateReadDto";
import UnmappedEndpointsReadDto from "../../../Models/Assets/UnmmapedEndpointsReadDto";
import GatewaysReadDto from "../../../Models/Assets/GatewaysReadDto";
import { RequirementReadDto } from "../../../Models/Requirement/RequirementReadDto";
import { MachineReadDto } from "../../../Models/Machine/MachinesReadDto";
/**
 * This service provides implementations for http calls to the api pertaining to device crud functionality
 */

class AssetsApiService {
  axiosInstance: AxiosInstance;
  constructor() {
    //Subscribe to identity store the get token
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_DEVICE_MANAGEMENT_SERVICE_URL,
    });
  }

  async getAllAssets() {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.AssetsV1}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data
  }

  async getSelectedAsset(assetId: string) {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.AssetsV1}/${assetId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data
  }

  async getAssetImages(imageId: string) {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.ImagesV1}/${imageId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetImageReadDto
  }

  async getTemplate(templateId: string) {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.AssetsV1}/template/${templateId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetTemplateReadDto
  }

  async getUnmappedEndpointDevices() {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.DevicesV1}/unmapped-endpoints`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as UnmappedEndpointsReadDto[];
  } 

  async getAllGateways() {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.DevicesV1}/gateways`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as GatewaysReadDto[];
  }

  async getAllLocations() {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Areas}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAllRequirements(): Promise<RequirementReadDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Requirement}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAllMachines() {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Machines}`,
      // api/assets/* is served by the AssetService — route it via the asset service URL
      // (identical to the device-management URL in deployed envs; lets it hit the local
      // AssetService in dev).
      baseURL: process.env.REACT_APP_ASSET_SERVICE_URL,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as Array<MachineReadDto>;
  }

  async createDefaultAsset(asset: any) {
    let response = await this.axiosInstance({
      method: "POST",
      url: `${ApiRoutes.Machines}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: asset,
    });
    return response.data as any;
  }

  async updateDefaultAsset(asset: any) {
    let response = await this.axiosInstance({
      method: "PUT",
      url: `${ApiRoutes.Machines}/${asset.id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: asset,
    });
    return response.data as any;
  }

  async createAsset(asset: any) {
    let response = await this.axiosInstance({
      method: "POST",
      url: `${ApiRoutes.AssetsV1}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: asset,
    });
    return response.data as any;
  }

  async updateAsset(asset: any) {
    let response = await this.axiosInstance({
      method: "PUT",
      url: `${ApiRoutes.AssetsV1}/${asset.id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: asset,
    });
    return response.data as any;
  }

  async deleteDefaultAsset(assetId: string) {
    let response = await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.Machines}/${assetId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as any;
  }
  

  async deleteAsset(assetId: string) {
    let response = await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.AssetsV1}/${assetId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as any;
  }
  

  static get instance() {
    return assetsApiService;
  }
}

const assetsApiService = new AssetsApiService();
export default assetsApiService;
